<template>
  <div class="article-page">
    <div class="container">
      <BaseBreadcrumbs :items="breadcrumbs" />

      <ArticleWithSidebar v-if="article" :article="article">
        <template #after>
          <ArticleSlider
            v-if="recommendations.length"
            title="Читайте также"
            :articles="recommendations"
          />
          <ArticleSlider
            v-if="editorsChoice.length"
            title="Выбор редакции"
            :articles="editorsChoice"
          />

          <OurServices />
        </template>
      </ArticleWithSidebar>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseBreadcrumbs } from '~/components/base-ui/breadcrumbs'
  import ArticleWithSidebar from '~/components/article/article-detail/ArticleWithSidebar.vue'
  import ArticleSlider from '~/components/article/article-detail/ArticleSlider.vue'
  import OurServices from '~/components/article/article-detail/OurServices.vue'
  import type { ArticleCard } from '~/models/articles'

  const route = useRoute()
  const { $services } = useNuxtApp()
  const slug = String(route.params.slug)

  const { data: article, error } = await useAsyncData(`article-${slug}`, () =>
    $services.articleService.detail.getBySlug(slug)
  )

  if (error.value || !article.value) {
    throw createError({ statusCode: 404, message: 'Статья не найдена' })
  }

  const { breadcrumbs } = await useBreadcrumbs({ rubricId: article.value.news_rubric?.id })

  const recommendations = ref<ArticleCard[]>([])
  const editorsChoice = ref<ArticleCard[]>([])

  const ogUrl = `${useExternalHost()}${route.fullPath}`

  useSeoMeta({
    title: article.value.name,
    ogType: 'article',
    ogTitle: article.value.name,
    ogImage: article.value.image,
    ogUrl,
  })

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.value.name,
          image: article.value.image,
          datePublished: article.value.publicationDate,
          author: article.value.author_name
            ? { '@type': 'Person', name: article.value.author_name }
            : undefined,
        }),
      },
    ],
  })

  onMounted(async () => {
    const excludeIds = article.value?.id ? [article.value.id] : []

    await Promise.allSettled([
      $services.articleService.detail.addViewCount(slug),
      $services.articleService.detail
        .getRecommendations(excludeIds)
        .then(data => (recommendations.value = data)),
      $services.articleService.detail
        .getEditorsChoice(excludeIds)
        .then(data => (editorsChoice.value = data)),
    ])
  })
</script>

<style lang="scss" scoped>
  .article-page {
    @extend %flex-column;

    gap: 24px;
    padding: 40px 0 64px;

    @include vp-767 {
      padding: 24px 0 40px;
    }

    > .container {
      @include container;

      @extend %flex-column;

      gap: 32px;
    }
  }
</style>
